import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, Trophy, Play, Youtube, Video, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { useWebSocketContext } from '../hooks/useWebSocket';
import { DetailSkeleton } from '../components/PageSkeletons';
import { PullToRefresh } from '../components/PullToRefresh';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BracketMatch = ({ game, onUpdateScore, canEdit }) => {
  const [editing, setEditing] = useState(false);
  const [score1, setScore1] = useState(game.team1_score || 0);
  const [score2, setScore2] = useState(game.team2_score || 0);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdateScore(game.id, score1, score2);
    setEditing(false);
    setSaving(false);
  };

  const getStatusColor = () => {
    switch (game.status) {
      case 'completed': return 'bg-green-500';
      case 'in_progress': return 'bg-yellow-500';
      default: return 'bg-gray-400';
    }
  };

  return (
    <div className="border rounded-lg p-3 min-w-[200px] bg-card shadow-sm" data-testid={`bracket-match-${game.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-2 h-2 rounded-full ${getStatusColor()}`} />
        <span className="text-xs text-muted-foreground">
          Round {game.round_number} - Match {game.match_number}
        </span>
      </div>
      
      {/* Team 1 */}
      <div className={`flex items-center justify-between p-2 rounded ${game.winner_id === game.team1_id ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted/50'}`}>
        <span className={`font-medium ${!game.team1_name ? 'text-muted-foreground italic' : ''}`}>
          {game.team1_name || 'TBD'}
        </span>
        {editing ? (
          <input
            type="number"
            className="w-12 text-center border rounded"
            value={score1}
            onChange={(e) => setScore1(parseInt(e.target.value) || 0)}
            min="0"
          />
        ) : (
          <span className="font-bold">{game.team1_score}</span>
        )}
      </div>
      
      <div className="text-center text-xs text-muted-foreground my-1">vs</div>
      
      {/* Team 2 */}
      <div className={`flex items-center justify-between p-2 rounded ${game.winner_id === game.team2_id ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted/50'}`}>
        <span className={`font-medium ${!game.team2_name ? 'text-muted-foreground italic' : ''}`}>
          {game.team2_name || 'TBD'}
        </span>
        {editing ? (
          <input
            type="number"
            className="w-12 text-center border rounded"
            value={score2}
            onChange={(e) => setScore2(parseInt(e.target.value) || 0)}
            min="0"
          />
        ) : (
          <span className="font-bold">{game.team2_score}</span>
        )}
      </div>

      {/* Actions */}
      {canEdit && game.team1_id && game.team2_id && game.status !== 'completed' && (
        <div className="mt-2 flex gap-2">
          {editing ? (
            <>
              <Button size="sm" className="flex-1" onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" className="w-full" onClick={() => setEditing(true)}>
              Enter Score
            </Button>
          )}
        </div>
      )}

      {game.status === 'completed' && game.winner_id && (
        <div className="mt-2 text-center">
          <Badge variant="outline" className="text-green-600">
            <Trophy className="h-3 w-3 mr-1" />
            Winner Advances
          </Badge>
        </div>
      )}
    </div>
  );
};

const BracketRound = ({ title, games, onUpdateScore, canEdit }) => (
  <div className="flex flex-col gap-4">
    <h3 className="font-semibold text-center text-sm text-muted-foreground">{title}</h3>
    <div className="flex flex-col gap-8 justify-around">
      {games.map((game) => (
        <BracketMatch 
          key={game.id} 
          game={game} 
          onUpdateScore={onUpdateScore}
          canEdit={canEdit}
        />
      ))}
    </div>
  </div>
);

export const TournamentBracket = () => {
  const { tournamentId } = useParams();
  const { token, user } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [bracket, setBracket] = useState(null);
  const [livestream, setLivestream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const { isConnected, subscribeToTournament, onBracketUpdate } = useWebSocketContext();
  const canEdit = user?.role === 'admin' || user?.role === 'organizer' || user?.role === 'official';

  useEffect(() => {
    fetchData();
  }, [tournamentId]);

  // Subscribe to tournament updates when component mounts
  useEffect(() => {
    if (tournamentId && isConnected) {
      subscribeToTournament(tournamentId);
    }
  }, [tournamentId, isConnected, subscribeToTournament]);

  // Listen for real-time bracket updates
  useEffect(() => {
    const unsubscribe = onBracketUpdate((message) => {
      if (message.tournament_id === tournamentId && message.update_type === 'bracket_score_update') {
        const { game_id, team1_score, team2_score, winner_id, winner_name, status } = message.data;
        
        // Update the bracket state with new scores
        setBracket(prevBracket => {
          if (!prevBracket?.bracket) return prevBracket;
          
          const updateGame = (games) => {
            if (!games) return games;
            const updated = {};
            for (const [round, roundGames] of Object.entries(games)) {
              updated[round] = roundGames.map(game => 
                game.id === game_id 
                  ? { ...game, team1_score, team2_score, winner_id, status }
                  : game
              );
            }
            return updated;
          };
          
          return {
            ...prevBracket,
            bracket: {
              ...prevBracket.bracket,
              winners: updateGame(prevBracket.bracket.winners),
              losers: updateGame(prevBracket.bracket.losers),
              finals: prevBracket.bracket.finals?.id === game_id 
                ? { ...prevBracket.bracket.finals, team1_score, team2_score, winner_id, status }
                : prevBracket.bracket.finals
            }
          };
        });
        
        toast.success(`${winner_name} wins!`, {
          description: `Score: ${team1_score} - ${team2_score}`
        });
      }
    });
    
    return unsubscribe;
  }, [tournamentId, onBracketUpdate]);

  const fetchData = async () => {
    try {
      const [tournamentRes, bracketRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`),
        axios.get(`${API}/tournaments/${tournamentId}/brackets`)
      ]);
      
      setTournament(tournamentRes.data);
      setBracket(bracketRes.data);
    } catch (error) {
      console.error('Error fetching bracket:', error);
      toast.error('Failed to load bracket');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBracket = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/tournaments/${tournamentId}/brackets/generate`, 
        { bracket_type: 'double_elimination' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Bracket generated successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate bracket');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateScore = async (gameId, score1, score2) => {
    try {
      await axios.put(`${API}/brackets/${gameId}/score`,
        { team1_score: score1, team2_score: score2 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Score updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update score');
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <DetailSkeleton />
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={fetchData}>
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">{tournament?.name} - Bracket</h1>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-muted-foreground">
              Double Elimination Tournament
            </p>
            {isConnected ? (
              <Badge variant="outline" className="text-green-500 border-green-500">
                <Wifi className="w-3 h-3 mr-1" />
                Live
              </Badge>
            ) : (
              <Badge variant="outline" className="text-gray-500">
                <WifiOff className="w-3 h-3 mr-1" />
                Offline
              </Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchData} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          {canEdit && !bracket?.has_bracket && (
            <Button onClick={handleGenerateBracket} disabled={generating}>
              {generating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Trophy className="mr-2 h-4 w-4" />
              )}
              Generate Bracket
            </Button>
          )}
        </div>
      </div>

      {/* Live Stream Embed */}
      {livestream?.has_stream && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Play className="h-5 w-5 text-red-500 animate-pulse" />
              Live Stream
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aspect-video">
              <iframe
                src={livestream.stream.embed_url}
                className="w-full h-full rounded-lg"
                allowFullScreen
                allow="autoplay; encrypted-media"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {!bracket?.has_bracket ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Trophy className="h-16 w-16 text-muted-foreground mb-4" />
            <h2 className="text-xl font-bold mb-2">No Bracket Generated Yet</h2>
            <p className="text-muted-foreground text-center mb-4">
              Register teams and generate the bracket to start the tournament.
            </p>
            {canEdit && (
              <Button onClick={handleGenerateBracket} disabled={generating}>
                {generating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Trophy className="mr-2 h-4 w-4" />
                )}
                Generate Double Elimination Bracket
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Winners Bracket */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className="bg-green-500">Winners Bracket</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto pb-4">
                <div className="flex gap-8 min-w-max">
                  {Object.keys(bracket.bracket.winners || {}).sort((a, b) => a - b).map((round) => (
                    <BracketRound
                      key={`winners-${round}`}
                      title={`Round ${round}`}
                      games={bracket.bracket.winners[round]}
                      onUpdateScore={handleUpdateScore}
                      canEdit={canEdit}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Losers Bracket */}
          {Object.keys(bracket.bracket.losers || {}).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Badge variant="secondary">Losers Bracket</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto pb-4">
                  <div className="flex gap-8 min-w-max">
                    {Object.keys(bracket.bracket.losers || {}).sort((a, b) => a - b).map((round) => (
                      <BracketRound
                        key={`losers-${round}`}
                        title={`Losers Round ${round}`}
                        games={bracket.bracket.losers[round]}
                        onUpdateScore={handleUpdateScore}
                        canEdit={canEdit}
                      />
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Grand Finals */}
          {bracket.bracket.finals && (
            <Card className="border-yellow-500 border-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-yellow-500" />
                  Grand Finals
                </CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center">
                <BracketMatch
                  game={bracket.bracket.finals}
                  onUpdateScore={handleUpdateScore}
                  canEdit={canEdit}
                />
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
    </PullToRefresh>
  );
};
